import React from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatNumber } from 'accounting-js';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import navigationPaths from 'engine/navigationPaths';
import { useDeleteCostInvoicing, useDeleteCostsInvoicing, useGetCostsInvoicingHeaders } from 'api';

import CustomNoRowsOverlay from 'components/noRowsOverlay/customNoRowsOverlay';
import { CustomTotalsComponent } from '../FooterTotals/FooterTotals';
import DataGridCell from 'components/dataGridCell';
import DataGridActionsMenu from 'components/dataGridActionsMenu';
import DataGridHeader from 'components/dataGridHeader';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';

import * as Styled from './Table.styles';
import { useImmer } from 'use-immer';
import { useGetCostsInvoicingHeadersCount } from 'api/endpoints/getCostsInvoicingHeadersCount';
import { DocumentGroupActionsMenu } from 'components/DocumentGroupActionsMenu/DocumentGroupActionsMenu';
import { MassActions } from 'components/ui';
import { Publish, Event } from 'utility/Events/Events';

dayjs.extend(utc);
dayjs.extend(timezone);

export const Table = ({ filters }) => {
  const navigate = useNavigate();

  const [deleteInvoiceGuids, setDeleteInvoiceGuids] = React.useState([]);
  const [toastState, setUserIndication] = React.useState({
    visible: false,
    text: '',
  });
  const [totalAmount, setTotalAmount] = React.useState(0);
  const [totalAmountNet, setTotalAmountNet] = React.useState(0);
  const [selectedInvoices, setSelectedInvoices] = React.useState([]);
  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);
  const [pagination, updatePagination] = useImmer({
    rowsPerPageOptions: [25],
    pageSize: 25,
    page: 1,
  });

  const { data: costHeadersData, refetch: refetchCostHeadersData } = useGetCostsInvoicingHeaders(
    {
      from: dayjs.utc(filters.fromDate).toISOString(),
      to: dayjs.utc(filters.toDate).endOf('day').toISOString(),
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
    },
    {},
  );

  const { data: totalCostHeaders, refetch: refetchTotalCostHeaders } = useGetCostsInvoicingHeadersCount(
    {
      from: dayjs.utc(filters.fromDate).toISOString(),
      to: dayjs.utc(filters.toDate).endOf('day').toISOString(),
    },
    {},
  );

  const { mutate: deleteCost, isLoading: isCostDeleting } = useDeleteCostInvoicing(
    deleteInvoiceGuids[0],
    {
      onSuccess: () => {
        refetchCostHeadersData();
        refetchTotalCostHeaders();
        setDeleteInvoiceGuids([]);
        setDeleteDialogConfirmationOpen(false);
        Publish(Event.onCostListNeedsRefresh);
      },
    },
    {},
  );

  const { mutate: deleteCosts, isLoading: isCostsDeleting } = useDeleteCostsInvoicing(
    deleteInvoiceGuids,
    {
      onSuccess: () => {
        refetchCostHeadersData();
        refetchTotalCostHeaders();
        setDeleteInvoiceGuids([]);
        setDeleteDialogConfirmationOpen(false);
        Publish(Event.onCostListNeedsRefresh);
      },
    },
    {},
  );

  React.useEffect(() => {
    onRowSelectionChange(selectedInvoices);
  }, [selectedInvoices]);

  const onRowSelectionChange = (ids) => {
    const selected = new Set(ids);
    const selectedInvoiceData = (costHeadersData || []).filter((row) => selected.has(row.id.toString()));
    var totalAmountCount = 0;
    var totalAmountNetCount = 0;
    if (selectedInvoiceData.length === 0) {
      // empty selection, count all invoices
      (costHeadersData || []).forEach((el) => {
        totalAmountCount = totalAmountCount + el.amountToPay;
        totalAmountNetCount = totalAmountNetCount + el.amountToPayNet;
      });
    } else {
      // some invoices selected, count only the selected once
      selectedInvoiceData.forEach((el) => {
        totalAmountCount = totalAmountCount + el.amountToPay;
        totalAmountNetCount = totalAmountNetCount + el.amountToPayNet;
      });
    }
    setTotalAmount(totalAmountCount);
    setTotalAmountNet(totalAmountNetCount);
  };

  function getItemIndex(data) {
    return data.api.getRowIndex(data.row.id) + data.api.state.pagination.page * data.api.state.pagination.pageSize + 1;
  }

  function getInvoiceNumber(data) {
    return data.row.fullNumber;
  }

  function getFormattedAmountToPayValue(data) {
    return format(data.row.amountToPay) + ' PLN';
  }

  function getFormattedAmountToPayNetValue(data) {
    return format(data.row.amountToPayNet) + ' PLN';
  }

  function formatInvoiceDate(cell) {
    return dayjs(cell.value).format('DD-MM-YYYY');
  }

  const handlePreviewClick = (e) => {
    navigate(navigationPaths().CostInvoicesFormPreview.replace(':costId', e), { state: { mode: 'preview', id: e } });
  };

  const handleEditClick = (e) => {
    navigate(navigationPaths().CostInvoicesFormEdit.replace(':costId', e));
  };

  const handleToastClose = () => {
    setUserIndication({
      visible: false,
      text: '',
    });
  };

  const format = (value) => {
    if (value == null) {
      return '0,00';
    }

    return formatNumber(value, { precision: 2, thousand: ' ', decimal: ',' });
  };

  const handleDeleteInvoiceClick = (id) => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteInvoiceGuids([id]);
  };

  const handleDeleteSelectedInvoicesClick = () => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteInvoiceGuids(selectedInvoices);
  };

  const handleDeleteInvoiceAgreed = () => {
    if (deleteInvoiceGuids.length === 1) {
      deleteCost();
    } else if (deleteInvoiceGuids.length > 1) {
      deleteCosts();
    }
  };

  const handleDeleteInvoiceDisagreed = () => {
    setDeleteInvoiceGuids([]);
    setDeleteDialogConfirmationOpen(false);
  };

  const renderColHeader = (params) => <DataGridHeader>{params.colDef.headerName}</DataGridHeader>;

  const handleMenuOptionClick = (optionId, rowId) => {
    switch (optionId) {
      case 'preview':
        handlePreviewClick(rowId);
        break;
      case 'delete':
        handleDeleteInvoiceClick(rowId);
        break;
      case 'edit':
        handleEditClick(rowId);
        break;
      default:
        break;
    }
  };

  const invoiceColumns = [
    {
      field: 'index',
      headerName: 'ID',
      filterable: false,
      sortable: true,
      width: 15,
      valueGetter: getItemIndex,
      renderCell: (params) => <DataGridCell>{params.value}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'fullNumber',
      headerName: 'Numer pełny',
      flex: 2,
      width: 200,
      filterable: false,
      sortable: true,
      type: 'string',
      renderCell: (params) => (
        <DataGridCell main rowId={params.row.id} itemClicked={handlePreviewClick}>
          {getInvoiceNumber(params)}
        </DataGridCell>
      ),
      renderHeader: renderColHeader,
    },
    {
      field: 'dateOfIssueUtc',
      headerName: 'Data wystawienia',
      width: 170,
      filterable: false,
      sortable: true,
      type: 'string',
      renderCell: (params) => <DataGridCell>{formatInvoiceDate(params)}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'amountToPayNet',
      headerName: 'Do zapłaty (netto)',
      width: 170,
      filterable: false,
      sortable: true,
      type: 'string',
      renderCell: (params) => <DataGridCell>{getFormattedAmountToPayNetValue(params)}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'amountToPay',
      headerName: 'Do zapłaty (brutto)',
      width: 170,
      filterable: false,
      sortable: true,
      type: 'string',
      renderCell: (params) => <DataGridCell>{getFormattedAmountToPayValue(params)}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Akcje',
      width: 120,
      cellClassName: 'actions',
      headerAlign: 'right',
      align: 'right',
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <DataGridActionsMenu
          preview
          remove
          removeSelected={selectedInvoices.length > 1}
          rowId={params.row.id}
          handleMenuOptionClick={handleMenuOptionClick}
        />
      ),
    },
  ];

  return (
    <Styled.Wrapper>
      {selectedInvoices.length > 0 && (
        <MassActions
          items={[
            {
              key: 'deleteSelected',
              label: 'Usuń zaznaczone',
              onClick: handleDeleteSelectedInvoicesClick,
            },
          ]}
        />
      )}

      {deleteDialogConfirmationOpen && (
        <DeleteListItemConfirmationModal
          id="delete-confirmation-dialog"
          onConfirm={handleDeleteInvoiceAgreed}
          onClose={handleDeleteInvoiceDisagreed}
          isSaving={isCostDeleting || isCostsDeleting}
          title={selectedInvoices?.length > 1 ? 'Usunąć koszty?' : 'Usunąć koszt?'}
          confirmText={selectedInvoices?.length > 1 ? 'Tak, usuń koszty' : 'Tak, usuń koszt'}
          message={
            selectedInvoices?.length > 1
              ? 'Czy na pewno chcesz usunąć koszty? Tej operacji nie będzie można cofnąć.'
              : 'Czy na pewno chcesz usunąć koszt? Tej operacji nie będzie można cofnąć.'
          }
        />
      )}

      <DataGrid
        sx={{ '& > .MuiDataGrid-columnSeparator': { visibility: 'hidden' }, border: 'none' }}
        name="costs"
        spacing={10}
        autoHeight={true}
        autoPageSize={false}
        rows={costHeadersData || []}
        rowHeight={64}
        getRowHeight={() => 64}
        columns={invoiceColumns}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        checkboxSelection
        pagination
        paginationMode="server"
        rowCount={totalCostHeaders}
        rowsPerPageOptions={pagination.rowsPerPageOptions}
        page={pagination.page - 1}
        pageSize={pagination.pageSize}
        onPageChange={(data) => {
          updatePagination((draft) => {
            draft.page = data + 1;
          });

          refetchCostHeadersData();
          refetchTotalCostHeaders();
        }}
        experimentalFeatures={{ newEditingApi: true }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'fullNumber', sort: 'asc' }],
          },
        }}
        components={{
          NoRowsOverlay: CustomNoRowsOverlay,
          Footer: CustomTotalsComponent,
        }}
        componentsProps={{ footer: { totalAmount, totalAmountNet } }}
        onSelectionModelChange={(ids) => setSelectedInvoices(ids)}
      />

      <Snackbar onClose={handleToastClose} open={toastState.visible} autoHideDuration={3000}>
        <Alert severity="success" sx={{ width: '100%' }}>
          {toastState.text}
        </Alert>
      </Snackbar>
    </Styled.Wrapper>
  );
};

Table.propTypes = {
  filters: PropTypes.object.isRequired,
};
